import type { Guardian } from '@/modules/guardians/guardians.types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import type { PaginatedResponse } from '@/lib/api-client'
import { http } from '@/lib/api-client'
import type { BillingIdType } from '@/modules/students/students.types'

export interface ContactInput {
  kind: 'person' | 'company'
  firstName: string
  lastName: string
  idType: BillingIdType
  idNumber: string
  email: string
  phone: string
  address: string
  notes: string
}
export interface Contact extends Guardian, Omit<
  ContactInput,
  'idNumber' | 'email' | 'phone' | 'address' | 'notes'
> {
  id: string
  createdAt: string
  idNumber: string | null
  email: string | null
  phone: string | null
  address: string | null
  notes: string | null
  _count?: { students: number; billingStudents: number }
}
interface LinkedStudent {
  gender: 'male' | 'female' | null
  dateOfBirth: string | null
  id: string
  firstName: string
  lastName: string
}
export interface ContactDetail extends Contact {
  students: {
    studentId: string
    relationship: string | null
    isPrimary: boolean
    student: LinkedStudent
  }[]
  billingStudents: LinkedStudent[]
}
export interface ContactFilters {
  q?: string
  kind?: string
  idType?: BillingIdType
  email?: 'present' | 'missing'
  phone?: 'present' | 'missing'
  sortBy?: 'name' | 'idNumber' | 'email' | 'phone'
  sortDir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}
export const contactName = (c: Pick<Contact, 'firstName' | 'lastName'>) =>
  `${c.firstName} ${c.lastName}`.trim()
export const contactApi = {
  list: (params: ContactFilters) =>
    http.get<PaginatedResponse<Contact>>('/contacts', { params }),
  get: (id: string) =>
    http.get<{ data: ContactDetail }>(`/contacts/${id}`).then((r) => r.data),
  save: (data: ContactInput, id?: string) =>
    (id
      ? http.put<{ data: Contact }>(`/contacts/${id}`, data)
      : http.post<{ data: Contact }>('/contacts', data)
    ).then((r) => r.data),
  setBilling: (studentId: string, contactId: string | null) =>
    http.put(`/students/${studentId}/billing-contact`, { contactId }),
}

// A shared contact edit affects every linked student, but never saved invoice snapshots.
export function useContactMutation<T>(
  mutationFn: (input: T) => Promise<unknown>,
  onSuccess?: () => void,
) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await Promise.all(
        [
          'contacts',
          'contact',
          'students',
          'student',
          'guardians',
          'guardian',
        ].map((key) => client.invalidateQueries({ queryKey: [key] })),
      )
      toast.success('Cambios guardados')
      onSuccess?.()
    },
    onError: (error: Error) => toast.error(error.message),
  })
}
